import { produce } from 'immer';
import { useCallback } from 'react';
import { analytics } from 'app/utils/segment';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { NodeStateUpdateType } from 'flow-builder/types/state';
import { getRootTree } from 'flow-builder/utils/node/getNodeTree/getNodeTree';
import { getScopeVars } from 'flow-builder/utils/scopeVars/getScopeVars/getScopeVars';
import { replaceNodeWithAction } from '../useDisconnect/useDisconnect';
export const useDeleteNodeSelection = ({ props, state, derived, }) => {
    const { flow } = props;
    const { nodeState, setNodeState, setHighlightState } = state;
    const { deletableSelection } = derived;
    const onDeleteSelection = useCallback(() => {
        const selection = deletableSelection.nodes;
        const [firstDeletableNodeId] = selection;
        const firstDeletableNode = nodeState.relations[firstDeletableNodeId];
        analytics.flowActionDeleted({
            flowType: flow.type,
            nodeType: selection.length === 1 ? firstDeletableNode.nodeType : 'MULTIPLE',
        });
        setHighlightState({
            path: [null],
        });
        setNodeState(produce((state) => {
            const { relations, connections, changes } = state;
            changes.unshift(NodeStateUpdateType.NodeDelete);
            const { root: selectionRootNode, orphans: selectionOutOfBoundsChildrenIds, } = selection.reduce((acc, id) => {
                const relation = relations[id];
                const { parentId, children } = relation;
                if (!acc.root &&
                    (parentId === null || selection.indexOf(parentId) === -1)) {
                    acc.root = relation;
                }
                const orphans = children.filter((id) => selection.indexOf(id) === -1);
                acc.orphans.push(...orphans);
                return acc;
            }, {
                root: null,
                orphans: [],
            });
            if (!selectionRootNode) {
                throw new Error("Couldn't find selection root node");
            }
            const { parentId } = selectionRootNode;
            if (parentId !== null) {
                replaceNodeWithAction(connections, relations, parentId, selectionRootNode.id, flow.attributes);
            }
            selection.forEach((id) => {
                delete relations[id];
                delete connections[id];
            });
            selectionOutOfBoundsChildrenIds.forEach((id) => {
                delete connections[id];
                // If it's a tag it won't exist in relations, so we only need to delete connection.
                if (!relations[id]) {
                    return;
                }
                relations[id].parentId = null;
                if (relations[id].nodeType === FlowNodeTypes.ACTION) {
                    delete relations[id];
                }
            });
            state.relations = relations;
            state.connections = connections;
            state.scopeVars = getScopeVars(flow.attributes, relations);
            state.rootTree = getRootTree(relations);
        }));
    }, [
        flow.type,
        flow.attributes,
        nodeState.relations,
        deletableSelection,
        setHighlightState,
        setNodeState,
    ]);
    return onDeleteSelection;
};
